import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { useEffect } from "react";

import { BRAND } from "@bigpp/config/brand";

const STORAGE_KEY = "bigpp:onboarded";

const hasSeenOnboarding = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

const markSeen = (): void => {
  try {
    localStorage.setItem(STORAGE_KEY, "1");
  } catch {
    // ignore
  }
};

export const startOnboarding = (): void => {
  const d = driver({
    showProgress: true,
    allowClose: true,
    overlayOpacity: 0.55,
    stagePadding: 4,
    stageRadius: 8,
    popoverClass: "bigpp-driver-popover",
    nextBtnText: "Next",
    prevBtnText: "Back",
    doneBtnText: "Get started",
    onDestroyed: markSeen,
    steps: [
      {
        popover: {
          title: `Welcome to ${BRAND}`,
          description: `${BRAND}, inside PowerPoint. A quick 30-second tour so you know where everything lives.`,
        },
      },
      {
        element: '[data-tour="composer"]',
        popover: {
          title: "Ask anything",
          description: `Type what you want ${BRAND} to do — rewrite titles, restructure the deck, translate slides. Drag files in as context.`,
          side: "top",
          align: "center",
        },
      },
      {
        element: '[data-tour="attach"]',
        popover: {
          title: "Bring context in",
          description: `Attach PDFs, images, or reference decks. ${BRAND} uses them as source material — quote from them, extract charts, or match the branding.`,
          side: "top",
          align: "start",
        },
      },
      {
        element: '[data-tour="dictate"]',
        popover: {
          title: "Dictate instead of type",
          description:
            "Hit the mic to speak your instructions. Handy when you're mid-review and don't want to break your train of thought.",
          side: "top",
          align: "start",
        },
      },
      {
        element: '[data-tour="quick-prompts"]',
        popover: {
          title: "Quick prompts",
          description:
            "One-tap starters for common jobs — reformat, translate, tighten titles. Press ⌘K anywhere to open the menu.",
          side: "top",
          align: "start",
        },
      },
      {
        element: '[data-tour="skills"]',
        popover: {
          title: "Skills",
          description: `Skills are reusable instructions that teach ${BRAND} how to perform tasks. It's how you can teach ${BRAND} to work with PowerPoint the way you want it to.\n\nPowerSlide comes with a set of predefined skills to help you get started. You can create skills here, or ask PowerSlide to create them for you!`,
          side: "top",
          align: "start",
        },
      },
      {
        element: '[data-tour="new-chat"]',
        popover: {
          title: "New chat",
          description: "Start a fresh conversation. Your current deck stays in context.",
          side: "bottom",
          align: "end",
        },
      },
      {
        popover: {
          title: "Roll back anytime",
          description: `
            <p>Every message you send saves a snapshot of your deck. On each of your past messages you'll see:</p>
            <div class="bigpp-demo-bubble">
              <div class="bigpp-demo-msg">Make the title slide bolder</div>
              <div class="bigpp-demo-actions">
                <span class="bigpp-demo-action">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="18" r="3"/><circle cx="6" cy="6" r="3"/><path d="M13 6h3a2 2 0 0 1 2 2v7"/><path d="M11 18H8a2 2 0 0 1-2-2V9"/></svg>
                  Compare
                </span>
                <span class="bigpp-demo-action">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l4 2"/></svg>
                  Revert deck to 10:24
                </span>
              </div>
            </div>
            <p>Compare the current deck to that version, or restore it in one click.</p>
          `,
        },
      },
      {
        element: '[data-tour="settings"]',
        popover: {
          title: "Credits & settings",
          description:
            "Check your balance, buy top-ups, switch themes, or sign out.",
          side: "bottom",
          align: "end",
        },
      },
      {
        popover: {
          title: "You're ready",
          description: `Try one of the starter prompts below to see ${BRAND} in action.`,
        },
      },
    ],
  });
  d.drive();
};

export const useOnboardingOnFirstOpen = (ready: boolean): void => {
  useEffect(() => {
    if (!ready) return;
    if (hasSeenOnboarding()) return;
    const t = window.setTimeout(() => {
      startOnboarding();
    }, 400);
    return () => {
      window.clearTimeout(t);
    };
  }, [ready]);
};
